import { ArrowRight, Sparkles } from 'lucide-react';

export default function HeroSimple({
  headline     = 'Welcome to KiddieJoy!',
  subline      = 'Discover amazing toys, cycles, and learning games for endless fun! 🚀',
  ctaText      = 'Shop Now',
  ctaUrl       = '#',
  secondaryCtaText = 'Explore All',
  secondaryCtaUrl  = '#',
  badgeText    = 'Free Shipping on orders over $50',
  badgeEmoji   = '🚚',
  emoji        = '🧸',
  highlightWord = 'KiddieJoy',
}) {
  const parts = headline.split(highlightWord);

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-primary via-accent1 to-accent2 min-h-[500px] flex items-center">
      {/* Background blobs */}
      <div className="absolute top-16 right-24 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-16 left-24 w-48 h-48 bg-secondary/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

          {/* Text */}
          <div className="text-center lg:text-left">
            {badgeText && (
              <div className="inline-flex items-center gap-2 px-5 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white font-bold text-sm mb-8 border border-white/30">
                <span className="text-lg">{badgeEmoji}</span>
                {badgeText}
              </div>
            )}

            <h1
              className="text-5xl md:text-6xl font-black text-white mb-6 leading-tight"
              style={{ textShadow: '0 4px 20px rgba(0,0,0,0.2)' }}
            >
              {parts[0]}
              {parts.length > 1 && (
                <span className="relative inline-block mx-2">
                  <span className="bg-gradient-to-r from-secondary via-yellow-300 to-secondary bg-clip-text text-transparent">
                    {highlightWord}
                  </span>
                  <Sparkles className="absolute -top-4 -right-4 w-5 h-5 text-secondary opacity-80" />
                </span>
              )}
              {parts[1] ?? ''}
            </h1>

            <p
              className="text-xl text-white/90 mb-10 leading-relaxed max-w-lg mx-auto lg:mx-0 font-bold"
              style={{ textShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
            >
              {subline}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a
                href={ctaUrl}
                className="group inline-flex items-center justify-center gap-2 bg-secondary hover:bg-yellow-300 text-textDark font-black text-lg px-10 py-5 rounded-full shadow-2xl transform hover:-translate-y-1 transition-all"
              >
                {ctaText}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href={secondaryCtaUrl}
                className="inline-flex items-center justify-center bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-bold text-lg px-10 py-5 rounded-full border-2 border-white/40 hover:border-white/60 transition-all transform hover:-translate-y-1"
              >
                {secondaryCtaText}
              </a>
            </div>
          </div>

          {/* Emoji art */}
          <div className="flex items-center justify-center">
            <div className="relative w-72 h-72">
              {/* Glow */}
              <div className="absolute inset-0 bg-white/20 rounded-full blur-3xl" />

              {/* Center emoji */}
              <div className="relative z-10 w-full h-full flex items-center justify-center text-[140px] leading-none select-none">
                {emoji}
              </div>

              {/* Orbiting decorations */}
              <div
                className="absolute -top-6 -right-6 w-20 h-20 bg-gradient-to-br from-secondary to-yellow-400 rounded-2xl flex items-center justify-center text-4xl shadow-xl border-4 border-white"
                style={{ animation: 'bounce 3s ease-in-out infinite' }}
              >
                🌟
              </div>
              <div
                className="absolute -bottom-6 -left-6 w-16 h-16 bg-gradient-to-br from-accent3 to-green-500 rounded-2xl flex items-center justify-center text-3xl shadow-xl border-4 border-white"
                style={{ animation: 'bounce 4s ease-in-out infinite', animationDelay: '0.5s' }}
              >
                🎁
              </div>
              <div
                className="absolute top-1/2 -right-10 w-14 h-14 bg-gradient-to-br from-accent2 to-blue-500 rounded-2xl flex items-center justify-center text-2xl shadow-xl border-4 border-white"
                style={{ animation: 'bounce 3.5s ease-in-out infinite', animationDelay: '1s' }}
              >
                ✨
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wave bottom */}
      <div className="absolute bottom-0 left-0 w-full pointer-events-none">
        <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="w-full h-16">
          <path d="M0,40 Q360,10 720,40 T1440,40 L1440,80 L0,80 Z" fill="#ffffff" opacity="1" />
        </svg>
      </div>
    </section>
  );
}
